import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, BarChart3, Database, Link2, Search, Users } from 'lucide-react';

import { useAdminIdentityAttention, useAdminIdentityAttentionMutations } from '../../hooks/useApi';
import type {
    AdminIdentityAttentionAuth0EmailConflict,
    AdminIdentitySyncStats,
} from '../../services/api';
import { AdminAuth0BulkSyncHub } from './AdminAuth0BulkSyncHub';
import {
    AdminDashboardPaginatedSection,
    AdminDashboardTabButton,
} from './AdminDashboardPrimitives';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const PAGE_SIZE = 25;
type AttentionTab =
    | 'sync-progress'
    | 'users-without-teams'
    | 'auth0-missing-db'
    | 'auth0-email-conflicts';

export function AdminIdentityAttention() {
    const [activeTab, setActiveTab] = useState<AttentionTab>('users-without-teams');
    const [usersWithoutTeamsPage, setUsersWithoutTeamsPage] = useState(1);
    const [auth0Page, setAuth0Page] = useState(1);
    const [auth0ConflictPage, setAuth0ConflictPage] = useState(1);
    const [auth0SearchDraft, setAuth0SearchDraft] = useState('');
    const [auth0SearchQuery, setAuth0SearchQuery] = useState('');
    const [syncingSubjectId, setSyncingSubjectId] = useState<string | null>(null);
    const [approvingEmail, setApprovingEmail] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const attentionParams = useMemo(() => {
        if (activeTab === 'auth0-missing-db') {
            return {
                query: auth0SearchQuery.trim() || undefined,
                page: auth0Page,
                pageSize: PAGE_SIZE,
            };
        }
        if (activeTab === 'auth0-email-conflicts') {
            return { page: auth0ConflictPage, pageSize: PAGE_SIZE };
        }
        return { page: usersWithoutTeamsPage, pageSize: PAGE_SIZE };
    }, [activeTab, auth0ConflictPage, auth0Page, auth0SearchQuery, usersWithoutTeamsPage]);
    const attentionQuery = useAdminIdentityAttention(attentionParams);
    const { approveAuth0IdentityGroupLink, syncAuth0UserToDb } =
        useAdminIdentityAttentionMutations();
    const usersWithoutTeams = attentionQuery.data?.usersWithoutTeams.items ?? [];
    const auth0OnlyUsers = attentionQuery.data?.auth0UsersMissingDbRows.items ?? [];
    const auth0EmailConflicts = attentionQuery.data?.auth0EmailConflicts.items ?? [];
    const usersWithoutTeamsPageData = attentionQuery.data?.usersWithoutTeams;
    const auth0PageData = attentionQuery.data?.auth0UsersMissingDbRows;
    const auth0EmailConflictsPageData = attentionQuery.data?.auth0EmailConflicts;
    const syncStats = attentionQuery.data?.syncStats;

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setAuth0SearchQuery(auth0SearchDraft);
            setAuth0Page(1);
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [auth0SearchDraft]);

    const handleSyncToDb = async (subjectId: string) => {
        setStatusMessage(null);
        setSyncingSubjectId(subjectId);
        try {
            const result = await syncAuth0UserToDb.mutateAsync(subjectId);
            setStatusMessage(
                result.success
                    ? 'Auth0 user synced to DB.'
                    : 'Sync was accepted, but no DB row was found yet.',
            );
        } catch (err) {
            setStatusMessage(err instanceof Error ? err.message : 'Failed to sync Auth0 user.');
        } finally {
            setSyncingSubjectId(null);
        }
    };

    const handleApproveIdentityGroupLink = async (
        conflict: AdminIdentityAttentionAuth0EmailConflict,
    ) => {
        setStatusMessage(null);
        setApprovingEmail(conflict.email);
        try {
            const result = await approveAuth0IdentityGroupLink.mutateAsync(conflict.email);
            setStatusMessage(
                result.success
                    ? `Linked ${result.linkedSubjectIds.length} Auth0 identities for ${conflict.email}; DB subject is now ${result.primarySubjectId}.`
                    : `No matching DB user was found for ${conflict.email}.`,
            );
        } catch (err) {
            setStatusMessage(
                err instanceof Error ? err.message : 'Failed to approve identity group link.',
            );
        } finally {
            setApprovingEmail(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <Link
                        to="/admin"
                        className="inline-flex items-center gap-1 text-sm font-medium text-[#276db0] hover:text-[#215892]"
                    >
                        <ArrowLeft className="size-4" />
                        Back to admin
                    </Link>
                    <h1 className="mt-2 text-2xl font-semibold text-neutral-950">
                        Identity Attention Center
                    </h1>
                    <p className="mt-1 text-sm text-neutral-500">
                        Triage identity records that need manual review during Auth0 migration.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => attentionQuery.refetch()}
                    disabled={attentionQuery.isFetching}
                >
                    {attentionQuery.isFetching ? 'Refreshing...' : 'Refresh'}
                </Button>
            </div>

            {attentionQuery.error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    {attentionQuery.error instanceof Error
                        ? attentionQuery.error.message
                        : 'Failed to load identity attention data.'}
                </div>
            )}

            {statusMessage && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-700">
                    {statusMessage}
                </div>
            )}

            <div className="admin-tab-switcher-track w-fit">
                <AdminDashboardTabButton
                    active={activeTab === 'sync-progress'}
                    onClick={() => setActiveTab('sync-progress')}
                    icon={BarChart3}
                    label="Sync Progress"
                />
                <AdminDashboardTabButton
                    active={activeTab === 'users-without-teams'}
                    onClick={() => setActiveTab('users-without-teams')}
                    icon={Users}
                    label="Users Without Teams"
                />
                <AdminDashboardTabButton
                    active={activeTab === 'auth0-missing-db'}
                    onClick={() => setActiveTab('auth0-missing-db')}
                    icon={Database}
                    label="Auth0 Missing DB Rows"
                />
                <AdminDashboardTabButton
                    active={activeTab === 'auth0-email-conflicts'}
                    onClick={() => setActiveTab('auth0-email-conflicts')}
                    icon={Link2}
                    label="Same Email Auth0 Identities"
                />
            </div>

            {activeTab === 'auth0-missing-db' && (
                <div className="rounded-xl border bg-white p-4 shadow-sm">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                        <Input
                            value={auth0SearchDraft}
                            onChange={(event) => setAuth0SearchDraft(event.target.value)}
                            placeholder="Search Auth0 users by email, name, or subject"
                            className="border-0 admin-field-fill pl-10 shadow-none focus-visible:ring-0"
                        />
                    </div>
                </div>
            )}

            {activeTab === 'sync-progress' && (
                <div className="space-y-4">
                    <SyncProgressSection stats={syncStats} />
                    <AdminAuth0BulkSyncHub />
                </div>
            )}

            {activeTab === 'users-without-teams' && (
                <PaginatedAttentionSection
                    pageData={usersWithoutTeamsPageData}
                    page={usersWithoutTeamsPage}
                    onPageChange={setUsersWithoutTeamsPage}
                >
                    <Card>
                        <CardContent className="space-y-4 p-4">
                            <SectionHeader
                                title="Users Without Active Teams"
                                count={usersWithoutTeamsPageData?.total ?? 0}
                                description="Oldest users appear first so admins can resolve migration leftovers in order."
                            />
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>User</TableHead>
                                        <TableHead>Subject</TableHead>
                                        <TableHead>Created</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {usersWithoutTeams.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={3}
                                                className="text-sm text-neutral-500"
                                            >
                                                No active DB users without teams.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        usersWithoutTeams.map((user) => (
                                            <TableRow key={user.id}>
                                                <TableCell>
                                                    <div className="font-medium text-neutral-950">
                                                        {user.name}
                                                    </div>
                                                    <div className="text-xs text-neutral-500">
                                                        {user.email}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-neutral-600">
                                                    {user.subjectId ?? 'Missing'}
                                                </TableCell>
                                                <TableCell className="text-sm text-neutral-600">
                                                    {new Date(user.dateCreated).toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </PaginatedAttentionSection>
            )}

            {activeTab === 'auth0-missing-db' && (
                <PaginatedAttentionSection
                    pageData={auth0PageData}
                    page={auth0Page}
                    onPageChange={setAuth0Page}
                >
                    <Card>
                        <CardContent className="space-y-4 p-4">
                            <SectionHeader
                                title="Auth0 Users Missing DB Rows"
                                count={auth0PageData?.total ?? 0}
                                description="Auth0 users that do not match a DB user by subject or email."
                            />
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>User</TableHead>
                                        <TableHead>Subject</TableHead>
                                        <TableHead>Created</TableHead>
                                        <TableHead className="text-right">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {auth0OnlyUsers.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={4}
                                                className="text-sm text-neutral-500"
                                            >
                                                No Auth0 users are missing DB rows.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        auth0OnlyUsers.map((user) => (
                                            <TableRow key={user.subjectId || user.email}>
                                                <TableCell>
                                                    <div className="font-medium text-neutral-950">
                                                        {user.name || user.email}
                                                    </div>
                                                    <div className="text-xs text-neutral-500">
                                                        {user.email || 'No email'}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-sm text-neutral-600">
                                                    {user.subjectId}
                                                </TableCell>
                                                <TableCell className="text-sm text-neutral-600">
                                                    {user.createdAt
                                                        ? new Date(user.createdAt).toLocaleString()
                                                        : 'Unknown'}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() =>
                                                            handleSyncToDb(user.subjectId)
                                                        }
                                                        disabled={
                                                            !user.subjectId ||
                                                            syncingSubjectId === user.subjectId
                                                        }
                                                    >
                                                        {syncingSubjectId === user.subjectId
                                                            ? 'Syncing...'
                                                            : 'Sync to DB'}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </PaginatedAttentionSection>
            )}

            {activeTab === 'auth0-email-conflicts' && (
                <PaginatedAttentionSection
                    pageData={auth0EmailConflictsPageData}
                    page={auth0ConflictPage}
                    onPageChange={setAuth0ConflictPage}
                >
                    <Card>
                        <CardContent className="space-y-4 p-4">
                            <SectionHeader
                                title="Auth0 Same-Email Identity Conflicts"
                                count={auth0EmailConflictsPageData?.total ?? 0}
                                description="Auth0 identities grouped by email. Approve linking the accounts together before retrying sync."
                            />
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Email</TableHead>
                                        <TableHead>DB User</TableHead>
                                        <TableHead>Auth0 Identities</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {auth0EmailConflicts.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={3}
                                                className="text-sm text-neutral-500"
                                            >
                                                No same-email Auth0 identity conflicts found.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        auth0EmailConflicts.map((conflict) => (
                                            <TableRow key={conflict.email}>
                                                <TableCell className="align-top">
                                                    <div className="font-medium text-neutral-950">
                                                        {conflict.email}
                                                    </div>
                                                    <div className="text-xs text-neutral-500">
                                                        {conflict.identities.length} Auth0
                                                        identities
                                                    </div>
                                                </TableCell>
                                                <TableCell className="align-top text-sm text-neutral-600">
                                                    {conflict.dbUser ? (
                                                        <div>
                                                            <div className="font-medium text-neutral-950">
                                                                {conflict.dbUser.name}
                                                            </div>
                                                            <div className="text-xs text-neutral-500">
                                                                Current subject:{' '}
                                                                {conflict.dbUser.subjectId ??
                                                                    'Missing'}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        'No DB user with this email'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="space-y-2">
                                                        {conflict.identities.map((identity) => (
                                                            <div
                                                                key={identity.subjectId}
                                                                className="rounded-lg border border-neutral-200 p-3"
                                                            >
                                                                <div>
                                                                    <div className="font-medium text-neutral-950">
                                                                        {identity.name ||
                                                                            identity.email}
                                                                    </div>
                                                                    <div className="text-xs text-neutral-500">
                                                                        {identity.subjectId}
                                                                    </div>
                                                                    <div className="text-xs text-neutral-500">
                                                                        {identity.connection ??
                                                                            identity.provider ??
                                                                            'Unknown connection'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <div className="flex justify-end">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() =>
                                                                    handleApproveIdentityGroupLink(
                                                                        conflict,
                                                                    )
                                                                }
                                                                disabled={
                                                                    !conflict.dbUser ||
                                                                    approvingEmail ===
                                                                        conflict.email
                                                                }
                                                            >
                                                                {approvingEmail === conflict.email
                                                                    ? 'Linking...'
                                                                    : 'Approve Account Link'}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </PaginatedAttentionSection>
            )}
        </div>
    );
}

function percent(part: number, total: number): number {
    if (total <= 0) {
        return 0;
    }
    return Math.round((part / total) * 100);
}

function SyncProgressSection({ stats }: { stats?: AdminIdentitySyncStats }) {
    const companyPercent = percent(stats?.companiesSynced ?? 0, stats?.activeCompaniesTotal ?? 0);
    const userPercent = percent(stats?.usersSynced ?? 0, stats?.activeUsersTotal ?? 0);

    return (
        <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
                <SyncStatCard
                    label="Company org coverage"
                    value={`${companyPercent}%`}
                    detail={`${stats?.companiesSynced ?? 0} of ${stats?.activeCompaniesTotal ?? 0} active companies have Auth0 org IDs`}
                />
                <SyncStatCard
                    label="User subject coverage"
                    value={`${userPercent}%`}
                    detail={`${stats?.usersSynced ?? 0} of ${stats?.activeUsersTotal ?? 0} active users have Auth0 subject IDs`}
                />
                <SyncStatCard
                    label="Unsynced team users"
                    value={String(stats?.unsyncedActiveCompanyUsers ?? 0)}
                    detail="Active users on active company teams without Auth0 subject IDs"
                />
            </div>
            <Card>
                <CardContent className="space-y-2 p-4 text-sm text-neutral-600">
                    <h2 className="text-lg font-semibold text-neutral-950">
                        DB-backed sync rollout summary
                    </h2>
                    <p>
                        These counts come from the WEVO database only. They intentionally avoid live
                        Auth0 sampling so this dashboard stays fast and avoids Management API rate
                        limit surprises.
                    </p>
                    <p>
                        Same-email identities across Auth0 connections appear in their own tab for
                        manual account-link approval. DB email duplicate counts are not shown
                        because WEVO enforces unique user emails in the database.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}

function SyncStatCard({ detail, label, value }: { detail: string; label: string; value: string }) {
    return (
        <Card>
            <CardContent className="space-y-2 p-4">
                <div className="text-sm font-medium text-neutral-500">{label}</div>
                <div className="text-3xl font-semibold text-neutral-950">{value}</div>
                <div className="text-sm text-neutral-500">{detail}</div>
            </CardContent>
        </Card>
    );
}

function PaginatedAttentionSection({
    children,
    onPageChange,
    page,
    pageData,
}: {
    children: JSX.Element;
    onPageChange: (page: number) => void;
    page: number;
    pageData?: { items: unknown[]; pageSize: number; total: number; totalPages: number };
}) {
    return (
        <AdminDashboardPaginatedSection
            rows={pageData?.items ?? []}
            page={page}
            pageSize={pageData?.pageSize ?? PAGE_SIZE}
            total={pageData?.total ?? 0}
            totalPages={pageData?.totalPages ?? 1}
            onPageChange={onPageChange}
            render={() => children}
        />
    );
}

function SectionHeader({
    count,
    description,
    title,
}: {
    count: number;
    description: string;
    title: string;
}) {
    return (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
                <h2 className="flex items-center gap-2 text-lg font-semibold text-neutral-950">
                    <AlertTriangle className="size-4 text-amber-600" />
                    {title}
                </h2>
                <p className="mt-1 text-sm text-neutral-500">{description}</p>
            </div>
            <span className="rounded-full bg-neutral-100 px-3 py-1 text-sm font-semibold text-neutral-700">
                {count}
            </span>
        </div>
    );
}
